import styled from 'styled-components';
import { Chip, ChipColorType } from '../../../../components/common/chip/Chip';
import { SvgFileNameType } from '../../../../interfaces/Element.interfaces';
import { getPriceChangedInfo } from '../../../../utils/getter/getPriceChangedInfo.util';

export enum LastYearWeeklyTrend {
  UP = 'UP',
  DOWN = 'DOWN',
  STABLE = 'STABLE',
  NO_DATA = 'NO_DATA',
}

interface TrendConfig {
  text: string;
  chipColor: ChipColorType;
  icon?: {
    name: SvgFileNameType;
    color: string;
  };
}

const getTrendConfigs = (isSmallCategory: boolean): Record<LastYearWeeklyTrend, TrendConfig> => ({
  [LastYearWeeklyTrend.UP]: {
    text: '올라갔어요',
    chipColor: 'secondary',
    icon: {
      name: 'ArrowUp',
      color: '#FF3049',
    },
  },
  [LastYearWeeklyTrend.DOWN]: {
    text: '내려갔어요',
    chipColor: 'primary',
    icon: {
      name: 'ArrowDown',
      color: '#445EFF',
    },
  },
  [LastYearWeeklyTrend.STABLE]: {
    text: '비슷했어요',
    chipColor: isSmallCategory ? 'grey' : 'white',
    icon: {
      name: 'ArrowRight',
      color: '#505363',
    },
  },
  [LastYearWeeklyTrend.NO_DATA]: {
    text: '거래가 없었어요',
    chipColor: 'grey',
  },
});

// TODO: API 스펙 확정나면 리팩토링
interface CropPriceSummaryCardProps {
  label: string;
  todayAveragePrice: number;
  previousPrice: number;
  changeAmount: number | null;
  changeRate: number | null;
  lastYearWeeklyTrend: LastYearWeeklyTrend;
  labelVariant?: 'bold' | 'normal';
}

export const CropPriceSummaryCard = ({
  label,
  todayAveragePrice,
  previousPrice,
  changeAmount,
  changeRate,
  lastYearWeeklyTrend,
  labelVariant = 'bold',
}: CropPriceSummaryCardProps) => {
  const config = getTrendConfigs(labelVariant === 'normal')[lastYearWeeklyTrend];
  const priceChangedInfo = getPriceChangedInfo(changeAmount);

  return (
    <>
      <PrimaryPriceIndicator>
        <PrimaryPriceLabel labelVariant={labelVariant}>{label}</PrimaryPriceLabel>
        <PrimaryPriceValue>
          {todayAveragePrice === 0 && <NoTrade>{'오늘 거래가 없어요'}</NoTrade>}
          {todayAveragePrice !== 0 && (
            <>
              <AveragePrice>{`${todayAveragePrice.toLocaleString()}원`}</AveragePrice>
              {previousPrice !== 0 && changeAmount !== null && changeRate !== null && (
                <PriceChange color={priceChangedInfo?.color ?? '#858894'}>
                  {`${priceChangedInfo?.sign}${Math.abs(changeAmount).toLocaleString()}(${Math.abs(changeRate).toFixed(
                    1,
                  )}%)`}
                </PriceChange>
              )}
            </>
          )}
        </PrimaryPriceValue>
      </PrimaryPriceIndicator>
      <YoYPriceComparison>
        <YoYPriceComparisonText>{'작년에는 7일후에'}</YoYPriceComparisonText>
        <Chip
          type={'round'}
          theme={'light'}
          color={config.chipColor}
          size={'md'}
          icon={
            config.icon
              ? {
                  name: config.icon.name,
                  size: 'Small',
                  position: 'left',
                  fill: config.icon.color,
                }
              : undefined
          }
        >
          {config.text}
        </Chip>
      </YoYPriceComparison>
    </>
  );
};

const PrimaryPriceIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const PrimaryPriceLabel = styled.span<{ labelVariant: 'bold' | 'normal' }>`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: ${({ labelVariant }) => (labelVariant === 'bold' ? 700 : 400)};
  line-height: 130%; /* 18.2px */
`;

const PrimaryPriceValue = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const AveragePrice = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;

const PriceChange = styled.span<{ color: string }>`
  color: ${({ color }) => color};
  text-align: center;

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;

const YoYPriceComparison = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 4px;
`;

const YoYPriceComparisonText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;

const NoTrade = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;
